// 이미지 리사이징 유틸리티
export const resizeImage = (
  file: File,
  maxSizeMB: number = 0.5
): Promise<File> => {
  return new Promise((resolve, reject) => {
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    // 파일이 이미 500KB 이하면 리사이징하지 않음
    if (file.size <= maxSizeBytes) {
      resolve(file);
      return;
    }

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // 원본 이미지 크기
      let { width, height } = img;

      // 최대 크기 계산 (가로세로 비율 유지)
      const maxDimension = 1200; // 최대 너비/높이를 1200px로 줄임
      if (width > height) {
        if (width > maxDimension) {
          height = (height * maxDimension) / width;
          width = maxDimension;
        }
      } else {
        if (height > maxDimension) {
          width = (width * maxDimension) / height;
          height = maxDimension;
        }
      }

      // Canvas 크기 설정
      canvas.width = width;
      canvas.height = height;

      // 이미지 그리기
      ctx?.drawImage(img, 0, 0, width, height);

      // 품질 조정으로 파일 크기 줄이기
      let quality = 0.8; // 초기 품질을 0.8로 낮춤
      let dataUrl: string;

      const tryCompress = () => {
        dataUrl = canvas.toDataURL("image/jpeg", quality);
        const base64Size = dataUrl.length * 0.75; // base64는 약 33% 더 큼

        if (base64Size > maxSizeBytes && quality > 0.05) {
          // 최소 품질을 0.05로 낮춤
          quality -= 0.1;
          tryCompress();
        } else {
          // base64를 Blob으로 변환
          const byteString = atob(dataUrl.split(",")[1]);
          const mimeString = dataUrl.split(",")[0].split(":")[1].split(";")[0];
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);

          for (let i = 0; i < byteString.length; i++) {
            ia[i] = byteString.charCodeAt(i);
          }

          const blob = new Blob([ab], { type: mimeString });
          const resizedFile = new File([blob], file.name, {
            type: mimeString,
            lastModified: Date.now(),
          });

          console.log(
            `이미지 리사이징 완료: ${file.size} -> ${
              resizedFile.size
            } bytes (${(resizedFile.size / 1024).toFixed(1)}KB)`
          );
          resolve(resizedFile);
        }
      };

      tryCompress();
    };

    img.onerror = () => {
      reject(new Error("이미지 로드에 실패했습니다."));
    };

    // File을 URL로 변환하여 이미지 로드
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  });
};

// 이미지 파일 유효성 검사
export const validateImageFile = (
  file: File
): { isValid: boolean; message?: string } => {
  // 파일 타입 검사
  if (!file.type.startsWith("image/")) {
    return { isValid: false, message: "이미지 파일만 업로드 가능합니다." };
  }

  // 파일 크기 검사 (50MB 제한)
  const maxSize = 50 * 1024 * 1024;
  if (file.size > maxSize) {
    return {
      isValid: false,
      message: "파일이 너무 큽니다. 50MB 이하의 파일만 업로드 가능합니다.",
    };
  }

  return { isValid: true };
};
